import React from 'react';
import { graphql } from 'gatsby';
import { Layout } from 'components/layout';
import { ContentList } from 'components/article-list';
import { ContentListClassNames, ContentListNode } from 'components/article-list/article-list';
import { PageContext } from 'gatsby/internal';
import { Breadcrumb, Breadcrumbs } from 'components/breadcrumbs/breadcrumbs';
import ShortUnderline from 'assets/icons/shortUnderline.svg';
import { DropdownElement, DropdownMenu } from 'components/drop-down-menu';
import { SEO } from 'components/seo';
import { ContentType } from 'utils/enums';
import * as styles from './all-articles.module.scss';

type AllArticlesProps = {
  pageContext: PageContext & {
    numPages: number,
    perPage: number,
    skip: number,
    currentPage: number
    categorySlug?: string
    allArticlesSlug: string
    categoryName: string
  }
  data: {
    allStrapiArticle: {
      nodes: ContentListNode[]
    }
    allStrapiCategory: {
      nodes: {
        slug: string
        value: string
        articles: {
          id: string
        }[]
      }[]
    }
    strapiSitewideSchema: {
      allArticlesTitle: string
      allArticlesDescription: string
    }
  },
};

const AllArticles = ({ data, pageContext }: AllArticlesProps) => {
  const breadcrumbs: Breadcrumb[] = [
    { name: 'Home page', slug: '/' },
    { name: `${pageContext.categoryName}`, slug: '' },
  ];
  const articlesListClassNames: ContentListClassNames = {
    wrapperClassName: styles.wrapperList,
    containerClassName: styles.articlesContainer,
    cardContainerClassName: styles.cardContainer,
    cardContentClassName: styles.cardContent,
    cardImageClassName: styles.cardImage,
    cardSubtitleClassName: styles.cardSubtitle,
    cardDescriptionClassName: styles.cardDescription,
  };
  const categories = data.allStrapiCategory.nodes;
  const categoryArray: DropdownElement[] = [];
  if (pageContext.categoryName && (pageContext.categorySlug || pageContext.allArticlesSlug)) {
    categoryArray.push({ value: pageContext.categoryName, slug: pageContext.categorySlug || pageContext.allArticlesSlug || '' });
  }
  categoryArray.push({ value: 'All articles', slug: 'all-articles' });
  categories.forEach((category) => {
    if (category?.articles?.length > 0) {
      categoryArray.push({ value: category.value, slug: category.slug });
    }
  });
  return (
    <Layout>
      <div className={styles.genericContainer}>

        <Breadcrumbs breadcrumbs={breadcrumbs} className={styles.breadcrumb} />

        <div className={styles.titleContainer}>
          <h1 className={styles.title}>Articles</h1>
          <ShortUnderline className={styles.underline} />
        </div>
        <div className={styles.dropdownContainer}>
          <DropdownMenu elements={categoryArray} />
        </div>

        {data.allStrapiArticle?.nodes && data.allStrapiArticle?.nodes.length > 0 && (
          <div className={styles.lastSection}>
            <ContentList
              currentPage={pageContext.currentPage}
              numPages={pageContext.numPages}
              items={data.allStrapiArticle.nodes}
              listType={ContentType.article}
              classNames={articlesListClassNames}
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export const query = graphql`
  query($categorySlug: String, $perPage: Int, $skip: Int) {
    allStrapiArticle(
      filter: {category: {slug: {eq: $categorySlug}}}
      skip: $skip
      limit: $perPage
      sort: {createdAt: DESC}
      ) {
      nodes {
        slug
        title
        description
        primaryImage {
          localFile {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        category {
          slug
        }
        subcategory {
          slug
        }
        createdAt
      }
    }
    allStrapiCategory(
      filter: {slug: {ne: $categorySlug}}
    ) {
      nodes {
        slug
        value
        articles {
          id
        }
      }
    }
    strapiSitewideSchema {
      allArticlesTitle
      allArticlesDescription
    }
  }
`;

export default AllArticles;

export const Head = ({ data, pageContext }: AllArticlesProps) => {
  const title = `${data.strapiSitewideSchema.allArticlesTitle} - Page ${pageContext.currentPage}`;
  return (
    <SEO
      ogSlug={`/all-articles${pageContext.currentPage !== 1 ? `/${pageContext.currentPage}` : '/'}`}
      description={data.strapiSitewideSchema.allArticlesDescription}
      title={title}
    />
  );
};
